import { useState } from "react";
import { LocalCombinationAccordion } from "@coworker/app/src/components/RoomSettings/Molecules/LocalCombinationAccordion";
import { useTranslation } from "@coworker/locales";
import {
  Article,
  SalesStopData,
  EdsData,
} from "@coworker/app/src/components/RoomSettings/types/article";
import { GreyStrip } from "@coworker/app/src/components/RoomSettings/Atoms/GreyStrip";
import styles from "./local-combination-items-list-view.module.css";
import { isEmptyCombinationArticle } from "@coworker/app/src/components/RoomSettings/Helpers/articleHelpers";
import { LocalCombinationsItemsListViewItem } from "@coworker/apprestructured/src/media-maintenance/components/LocalCombinations/LocalCombinationsItemsListViewItem/LocalCombinationsItemsListViewItem";
import { MediaItemViewType } from "@coworker/apprestructured/src/media-maintenance/types/mediaItemViewType";

type LocalCombinationItemsListViewProps = {
  parentArticle: Article;
  combinationArticles: { [key: string]: Article[] };
  edsData: EdsData[] | null;
  salesStopData: SalesStopData[] | undefined;
  mediaItemViewType: MediaItemViewType;
};

export const LocalCombinationItemsListView = ({
  parentArticle,
  combinationArticles,
  edsData,
  salesStopData,
  mediaItemViewType = MediaItemViewType.SIMPLE_ITEM_VIEW,
}: LocalCombinationItemsListViewProps) => {
  const { t } = useTranslation();
  const [openAccordions, setOpenAccordions] = useState<{
    [key: string]: boolean;
  }>({}); // State to track open accordions

  const toggleAccordion = (id: string) => {
    setOpenAccordions((prev) => ({
      ...prev,
      [id]: !prev[id], // Toggle the state of the specific accordion
    }));
  };

  const articleCount =
    combinationArticles?.[parentArticle?.productArticleId || ""]?.length || 0;

  const piecesCount =
    combinationArticles?.[parentArticle?.productArticleId || ""]?.reduce(
      (acc, article) => acc + article.nbrArticles,
      0
    ) || 0;

  if (!parentArticle) {
    return null;
  }

  const isDeletedLocalCombination = isEmptyCombinationArticle(parentArticle);
  return (
    <>
      <LocalCombinationAccordion
        showInfoIcon={isDeletedLocalCombination}
        key={`local-combination-${parentArticle.id}`}
        isOpen={openAccordions[parentArticle.id] ?? false} // Check if the accordion is open
        title={
          isDeletedLocalCombination
            ? t("localCombinationDeletedString")
            : t("localCombinationCountString", {
                count: parentArticle.articlesCountInCombination,
              })
        }
        onClick={
          isDeletedLocalCombination
            ? () => {}
            : () => {
                toggleAccordion(parentArticle.id);
              }
        }
      />
      {openAccordions[parentArticle.id] && (
        <>
          <div className={styles["list-view-item-root"]}>
            {combinationArticles?.[parentArticle.productArticleId]?.map(
              (article: Article) => (
                <LocalCombinationsItemsListViewItem
                  mediaItemViewType={mediaItemViewType}
                  itemNo={article.productArticleId}
                  article={article}
                  totalQuantityPieces={
                    (article.articlesCountInCombination ?? 1) *
                    parentArticle.nbrArticles
                  }
                  edsData={
                    edsData?.find(
                      (eds: { productArticleId: string }) =>
                        eds.productArticleId === article.productArticleId
                    ) ?? null
                  }
                  salesStopData={
                    salesStopData?.find(
                      (salesStop) =>
                        salesStop.productArticleId === article.productArticleId
                    ) ?? null
                  }
                />
              )
            )}
          </div>
          <div className={styles["total-container"]}>
            <GreyStrip
              text={`${t("localCombinationGreyStripArticleString", {
                count: articleCount,
              })}, ${t("localCombinationGreyStripCountString", {
                count: piecesCount,
              })}`}
              bold={false}
            />
          </div>
        </>
      )}
    </>
  );
};
